import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchUsage as fetchUsageApi,
  fetchUsageHistory as fetchUsageHistoryApi,
  fetchUsageBreakdown as fetchUsageBreakdownApi,
  fetchUsageAlerts as fetchUsageAlertsApi,
  acknowledgeUsageAlert as acknowledgeUsageAlertApi,
} from "../../services/Usage";

export const fetchUsage = createAsyncThunk(
  "usage/fetchUsage",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchUsageApi();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchUsageHistory = createAsyncThunk(
  "usage/fetchUsageHistory",
  async ({ days = 30, type = "daily" } = {}, { rejectWithValue }) => {
    try {
      const response = await fetchUsageHistoryApi(days, type);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchUsageBreakdown = createAsyncThunk(
  "usage/fetchUsageBreakdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchUsageBreakdownApi();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const fetchUsageAlerts = createAsyncThunk(
  "usage/fetchUsageAlerts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchUsageAlertsApi();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

export const acknowledgeAlert = createAsyncThunk(
  "usage/acknowledgeAlert",
  async (alertId, { rejectWithValue }) => {
    try {
      await acknowledgeUsageAlertApi(alertId);
      return alertId;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

const usageSlice = createSlice({
  name: "usage",
  initialState: {
    usage: null,
    history: [],
    breakdown: [],
    alerts: [],
    loading: false,
    historyLoading: false,
    breakdownLoading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchUsage
      .addCase(fetchUsage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsage.fulfilled, (state, action) => {
        state.loading = false;
        state.usage = action.payload.data || null;
      })
      .addCase(fetchUsage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      // fetchUsageHistory
      .addCase(fetchUsageHistory.pending, (state) => {
        state.historyLoading = true;
      })
      .addCase(fetchUsageHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.history = action.payload.data || [];
      })
      .addCase(fetchUsageHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.error = action.payload || action.error.message;
      })
      // fetchUsageBreakdown
      .addCase(fetchUsageBreakdown.pending, (state) => {
        state.breakdownLoading = true;
      })
      .addCase(fetchUsageBreakdown.fulfilled, (state, action) => {
        state.breakdownLoading = false;
        state.breakdown = action.payload.data || [];
      })
      .addCase(fetchUsageBreakdown.rejected, (state, action) => {
        state.breakdownLoading = false;
        state.error = action.payload || action.error.message;
      })
      // fetchUsageAlerts
      .addCase(fetchUsageAlerts.fulfilled, (state, action) => {
        state.alerts = action.payload.data || [];
      })
      // acknowledgeAlert
      .addCase(acknowledgeAlert.fulfilled, (state, action) => {
        state.alerts = state.alerts.filter((a) => a.id !== action.payload);
      });
  },
});

export default usageSlice.reducer;
